import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '../RequestAction';
import CacheRepository from '../CacheRepository';
import { Folder, Nullable } from '@Models';
import { RequestFoldersSuccess, RequestFolderSuccess } from './response';
import { ContestMapper } from '@Mapper/tournament/ContestMapper';
import { FolderMapper } from '@Mapper/builder/folder.mapper';
import { IMapper } from '@Mapper/IMapper';
import { folder } from '@Api';
import { RequestContestSuccess } from '@Repository/tournament/response/RequestContestSuccess';
import { ApiClient } from '@Api';
import { folder_all } from '@Api';
import { DeleteRepository } from '../DeleteRepository';

@injectable()
export class FolderRepository
  extends CacheRepository<string, folder_all, folder, Folder>
  implements DeleteRepository<string>
{
  @inject(TYPES.FolderMapper) private _folderMapper!: FolderMapper;
  @inject(TYPES.Mapper.ContestMapper) private _contestMapper!: ContestMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;

  constructor() {
    super({ key: `/builder/folders` });
  }

  public async getFolders({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const foldersDto = await this._apiClient.builder.getFolders({
        locale: locale ?? this._locale.getlocale(),
      });
      await this.setCachedEntities({ data: foldersDto, locale: locale });
      return new RequestFoldersSuccess({
        payload: foldersDto.map((item) => this._folderMapper.map(item)),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async create({
    folder,
    locale,
  }: {
    folder: Folder;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const body = this._folderMapper.mapDto(folder);

      const dto = await this._apiClient.builder.createFolder({
        locale: locale ?? this._locale.getlocale(),
        requestBody: body,
      });
      await this.setCachedEntity({ data: dto, locale: locale });

      return new RequestFolderSuccess({ payload: this._folderMapper.map(dto) });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getFolder({
    folderId,
    locale,
  }: {
    folderId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const dto = await this._apiClient.builder.getFolder({
        locale: locale ?? this._locale.getlocale(),
        id: folderId,
      });
      await this.setCachedEntity({ data: dto, locale: locale });
      return new RequestFolderSuccess({
        payload: this._folderMapper.mapFull(dto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async delete({
    id,
    locale,
  }: {
    id: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      await this._apiClient.builder.deleteFolder({
        locale: locale ?? this._locale.getlocale(),
        id: id,
      });
      await this.removeCachedEntity({ entityId: id, locale: locale });
      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async update({
    folder,
    locale,
  }: {
    folder: Folder;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const requestBody = this._folderMapper.mapDto(folder);

      const folderDto = await this._apiClient.builder.updateFolder({
        locale: locale ?? this._locale.getlocale(),
        id: folder.id,
        requestBody: requestBody,
      });
      await this.setCachedEntity({ data: folderDto, locale: locale });
      return new RequestFolderSuccess({
        payload: this._folderMapper.mapFull(folderDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async toEvent({
    id,
    locale,
  }: {
    id: string;
    locale?: string;
  }): Promise<RequestAction> {
    try {
      const contestDto = await this._apiClient.builder.folderToEvent({
        locale: locale ?? this._locale.getlocale(),
        id: id,
      });
      return new RequestContestSuccess({
        payload: this._contestMapper.mapFull(contestDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async addArmyList({
    id,
    locale,
    armyListId,
  }: {
    id: string;
    locale?: string;
    armyListId: string;
  }): Promise<RequestAction> {
    try {
      await this._apiClient.builder.folderAddArmyList({
        locale: locale ?? this._locale.getlocale(),
        id: id,
        armyListId: armyListId,
      });
      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  protected getMapper(): IMapper<string, folder, folder, Folder> {
    return this._folderMapper;
  }
}
